import React, { useContext, useState, useEffect, useRef, useCallback, SyntheticEvent } from 'react';
import { useTranslation } from 'react-i18next';
import fileDownload from 'js-file-download';
import { pdf } from '@react-pdf/renderer';
import QRCode from 'qrcode';

// OpenLayers
import OlFeature from 'ol/Feature';
import OlFormatWKB from 'ol/format/WKB';
import { LineString } from 'ol/geom';

// MUI
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { IconButton, useMediaQuery, useTheme } from '@mui/material';

// MUI Icons
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FlipCameraAndroidIcon from '@mui/icons-material/FlipCameraAndroid';

// Custom Components
import DraggableList from '@/ui/DraggableList/DraggableList';
import mapService from '@/services/mapService';
import {
  InfoPaneActionButtons,
  InfoPaneCommentActionButton,
  InfoPaneShareActionButton,
  InfoPaneGPXActionButton,
  InfoPanePDFActionButton,
} from '../PublicActionButtons';
import SidebarInfoBoxStaza from '@/ui/PublicPage/SidebarInfoBoxStaza';
import PaneWarnings from '@/ui/PublicPage/PaneWarnings';
import PublicWarningDialog from '@/views/GIS/MapPage/PublicWarningDialog';
import RouteDocument from '@/components/PdfDocuments/RouteDocument';
import generateImageSection from '@/lib/pdfMapImageGenerator';
import api from '@/lib/api';
import { areWarningsIdentical, getStoredRouteWarnings, parseRouteWarnings, shuffleWarnings, storeRouteWarnings } from '@/lib/routeWarnings';
import useRouting from '@/components/MapRouting/useRouting';
import MapRoutingContext, { MapRoutingContexType } from '@/components/MapRoutingContext/MapRoutingContext';
import MapRouteProfile from '@/components/MapRouting/MapRouteProfile';
import GPXBuilderOL from '@/lib/gpx/gpxBuilderOL';

// Context
import MapContext from '@/components/Map/MapContext';
import SnackbarContext from '@/ui/SnackbarContext/SnackbarContext';
import LoaderContext from '@/components/LoaderContext/LoaderContext';
import DialogContext from '@/ui/DialogContext/DialogContext';

// Types
import { MapContextType } from '@/@types/context/MapContext';
import { DCRecord } from '@/@types/lib/dataController';
import { RouteCode } from '@/@types/services/mapService';
import MapRouteInfo from '@/components/MapRouting/MapRouteInfo';
import MapRouteBoxNoPoints from '@/components/MapRouting/MapRouteBoxNoPoints';
import MapRouteBoxOnePoint from '@/components/MapRouting/MapRouteBoxOnePoint';
import MapRouteBoxNoRoute from '@/components/MapRouting/MapRouteBoxNoRoute';
import MapRouteSelector from '@/components/MapRouting/MapRouteSelector';

export type RoutePoint = {
  coord: number[];
  label: string;
  uuid: string;
};

type PublicRoutesPaneProps = {
  handleShowCommentPane: (newCommentPane: object) => void;
  drawerStage: number;
  pane: number | undefined;
  open: boolean;
  layoutMode?: 'desktop' | 'mobile';
};

const LINE = 'LineString';
const POLY = 'Polygon';
const PT = 'Point';

function PublicRoutesPane(props: PublicRoutesPaneProps) {
  const mapContext = useContext(MapContext) as MapContextType;
  const snackbarContext = useContext(SnackbarContext);
  const loaderContext = useContext(LoaderContext);
  const dialogContext = useContext(DialogContext);
  const mapRoutingContext = useContext(MapRoutingContext) as MapRoutingContexType;

  const { routingPoints, setRoutingPoints } = mapRoutingContext;

  const { routeData, drawFirstPoint, drawRoute, clearRoute, getRouteExtent, deleteFromRoute, clearAllRouteData } = useRouting({});

  const [oldDrawerStage, setOldDrawerStage] = useState(1);

  const [routes, setRoutes] = useState<{
    records: DCRecord[];
    features: OlFeature<LineString>[];
  }>({
    records: [],
    features: [],
  });

  const [selectedRouteCode, setSelectedRouteCode] = useState<RouteCode>('A');
  const [routeFound, setRouteFound] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const theme = useTheme();
  const isMobile: boolean = useMediaQuery(theme.breakpoints.down('md'));

  const { t } = useTranslation();

  const { handleShowCommentPane, open, pane, drawerStage, layoutMode } = props;

  const handleClear = () => {
    setRoutes({ records: [], features: [] });
    setRouteFound(false);
    clearRoute();
  };

  useEffect(() => {
    localStorage.setItem('route_points', JSON.stringify(routingPoints));

    handleClear();

    if (mapService.getRoute && routingPoints.length > 1) {
      loaderContext.toggleLoading(true);
      setIsLoading(true);

      const lineStringGeometry = new LineString(routingPoints.map((x: RoutePoint) => x.coord));

      if (lineStringGeometry instanceof LineString) {
        const lineStringFeature = new OlFeature({
          type: LINE,
          geometry: lineStringGeometry,
        });

        mapService
          .getRoute(lineStringFeature)
          .then((ri) => {
            // features for the route linestring
            if (ri === null) {
              snackbarContext.showNotification(t('notifications.no_route_found'), 'error');
              return;
            }
            const wktFormatter = new OlFormatWKB();
            const retrievedRecords = ri as DCRecord[];
            const features = retrievedRecords?.map((x) => {
              const geom = wktFormatter.readGeometry(x.wkb as string, {
                dataProjection: 'EPSG:3765',
                featureProjection: 'EPSG:3857',
              });
              const feat: OlFeature<LineString> = new OlFeature<LineString>({
                type: LINE,
                geometry: geom as LineString,
              });
              feat.set('recordId', x.id);
              feat.set('code', x.code);
              return feat;
            });

            setRoutes({ records: retrievedRecords, features });

            if (retrievedRecords.length === 1) {
              drawRoute({
                record: retrievedRecords[0],
                feature: features[0],
              });
            }
            setRouteFound(true);
          })
          .catch(() => {
            // snackbarContext.showNotification(t("notifications.no_route_found"), "error");
            setRouteFound(false);
            return;
          })
          .finally(() => {
            loaderContext.toggleLoading(false);
            setIsLoading(false);
          });
      }
    } else if (routingPoints.length === 1) {
      drawFirstPoint(routingPoints[0]);
    }
  }, [routingPoints]);

  useEffect(() => {
    const rec = routes.records.find((x) => x.code === selectedRouteCode);
    const feat = routes.features.find((x) => x.get('recordId') === rec?.id && x.get('code') === selectedRouteCode);
    if (rec && feat) {
      drawRoute({ record: rec, feature: feat });
    }
  }, [selectedRouteCode, routes]);

  const handleDownloadGPX = useCallback(
    (evt: SyntheticEvent) => {
      const rec = routes.records.find((x) => x.code === selectedRouteCode);
      const feat = routes.features.find((x) => x.get('recordId') === rec?.id && x.get('code') === selectedRouteCode);

      if (rec && feat) {
        const gpxBuilder = new GPXBuilderOL();
        gpxBuilder.addStazaFromRoute(rec, feat, routingPoints);
        const gpx = gpxBuilder.writeGPX();
        if (gpx) {
          const file = new Blob([gpx], { type: 'application/gpx+xml' });
          fileDownload(file, 'ruta.gpx');
        }
      }
    },
    [routeData]
  );

  const generateShareUrl = () => {
    const apiInstance = new api();

    if (!mapContext || !mapContext.map) return '';

    if (routeData?.feature) {
      const { feature } = routeData;
      const extent = feature.getGeometry()?.getExtent();

      const maplayers = localStorage.getItem('maplayers');

      const config = {
        extent,
        maplayers,
        route_points: routingPoints,
      };

      const data = {
        config: JSON.stringify(config),
        object_id: routeData?.record?.id,
        object_type: 'route',
      };

      return apiInstance.Call('share-generate', 'post', data).then((resp) => {
        // @ts-ignore
        if (resp.success && resp?.data?.url_hash) {
          // @ts-ignore
          const shareUrl = `${window.location.origin}/share?hash=${resp.data.url_hash}`;
          // console.log("ShareURL:", shareUrl);
          return shareUrl;
        }

        return '';
      });
    }
    return '';
  };

  const handleDownloadPDF = useCallback(
    async (evt: SyntheticEvent) => {
      if (routeData && mapContext.map && routingPoints) {
        const view = mapContext.map.getView();
        if (routeData.feature.getGeometry()?.getExtent() !== undefined) {
          const extent = routeData.feature.getGeometry()?.getExtent();

          const mapSize = mapContext.map.getSize();
          const mapHeight = mapSize ? mapSize[1] : 1000; // shouldn't ever be undefined
          const mapWidth = mapSize ? mapSize[0] : 1920;

          const mapWider = mapWidth >= mapHeight;

          let padding = [0, 0, 0, 0];
          const offset = 20;

          if (mapWider) {
            const paddingX = (mapWidth - mapHeight) / 2 + offset;
            padding = [2 * offset, paddingX, offset, paddingX];
          } else {
            const paddingY = (mapHeight - mapWidth) / 2 + offset;
            padding = [paddingY + offset, offset, paddingY, offset];
          }

          if (extent)
            view.fit(extent, {
              padding: padding,
              maxZoom: 20,
            });

          const shareUrl = await generateShareUrl();
          generateImageSection(mapContext.map)
            .then(async (mapImage) => {
              QRCode.toDataURL(shareUrl, { width: 300, margin: 2, color: { dark: '#005a96' } }, async (err, url) => {
                if (err) {
                  console.error(err);
                  return;
                }

                // @ts-ignore
                const chartId = layoutMode ? `route-profile-chart-${layoutMode}` : 'route-profile-chart';
                // @ts-ignore
                const chartInstance = window.Apex._chartInstances.find((chart: any) => chart.id === chartId);

                const base64 = await chartInstance.chart.dataURI();

                const document = (
                  <RouteDocument
                    selectedRoute={routeData}
                    routePoints={routingPoints}
                    mapImage={mapImage.image as string}
                    qrCodeUrl={url}
                    graphURI={base64.imgURI}
                  />
                );

                const file = await pdf(document).toBlob();
                fileDownload(file, 'route_' + new Date().valueOf() + '.pdf');
              });
            })
            .finally(() => {
              const defaultPadding = isMobile ? [20, 20, 300, 20] : [20, 20, 20, 20];
              if (extent)
                view.fit(extent, {
                  padding: defaultPadding,
                  maxZoom: 15,
                });
            });
        }
      }
    },
    [routeData, layoutMode]
  );

  const handleCancelRouting = (evt: React.MouseEvent<HTMLElement>) => {
    clearAllRouteData();
    setRouteFound(false);
    history.pushState(null, '', '/');
  };

  const handleReverseRouting = (evt: React.MouseEvent<HTMLElement>) => {
    const reversePoints = [...routingPoints].reverse();
    setRoutingPoints(reversePoints);
  };

  useEffect(() => {
    if (routeFound === false) return;
    if ((pane === 1 && open && drawerStage === oldDrawerStage) || (isMobile && pane === 1 && oldDrawerStage === 0 && drawerStage !== 0)) {
      // focus on feat
      if (mapContext && mapContext.map) {
        const view = mapContext.map.getView();
        const extent = getRouteExtent();
        const padding = isMobile ? [20, 20, 300, 20] : [20, 20, 20, 20];
        if (extent) {
          view.fit(extent, { padding, duration: 500, maxZoom: 15 });
        }
      }
    }

    setOldDrawerStage(drawerStage);
  }, [open, pane, drawerStage, routeFound]);

  useEffect(() => {
    if (routeFound && routeData) {
      const parsedWarnings = parseRouteWarnings(routeData.record);
      const parsedWarningMSP = parsedWarnings.warningsMSP;
      const parsedWarningPodrucja = shuffleWarnings(parsedWarnings.warningsPodrucja);
      const previousWarnings = getStoredRouteWarnings();

      const areAreaWarningsIdentical = areWarningsIdentical(previousWarnings.warningsPodrucja, parsedWarningPodrucja);
      const areMSPWarningsIdentical = areWarningsIdentical(previousWarnings.warningsMSP, parsedWarningMSP);

      storeRouteWarnings(parsedWarningMSP, parsedWarningPodrucja);

      const warnings = [...parsedWarningMSP, ...parsedWarningPodrucja];
      if (warnings.length > 0 && (!areMSPWarningsIdentical || !areAreaWarningsIdentical)) {
        dialogContext.showDialog(PublicWarningDialog, {
          warnings,
          onClose: () => {
            dialogContext.hideDialog();
          },
        });
      }
    }
  }, [routeFound, routeData]);

  const routeA = routes.records.find((x) => x.code === 'A'); // preporucena
  const routeB = routes.records.find((x) => x.code === 'B'); // najkraca

  const showRouteA = routeA && routeB && routeA.hash !== routeB.hash;

  const notEnoughPoints = routingPoints.length <= 1;
  const onlyOneRoute = !showRouteA;
  const routeWarnings = routeData ? parseRouteWarnings(routeData.record).warnings : [];

  return (
    <Box height="100%" width="100%">
      <Box p={2}>
        {routingPoints.length > 0 ? (
          <Box sx={{ display: 'flex', pb: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {showRouteA ? (
                <MapRouteSelector
                  selectedRouteCode={selectedRouteCode}
                  onSelectRoute={setSelectedRouteCode}
                  disabled={notEnoughPoints || onlyOneRoute}
                />
              ) : null}
            </Box>
            <span style={{ flex: 1 }} />
            <Tooltip title={t('buttons.reverse_points')}>
              <span>
                <IconButton onClick={handleReverseRouting} sx={{ pr: 1 }} size="small" disabled={routingPoints.length < 2}>
                  <FlipCameraAndroidIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('buttons.remove_all_points')}>
              <IconButton onClick={handleCancelRouting} sx={{ pr: 1 }} size="small">
                <DeleteForeverIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ) : (
          <MapRouteBoxNoPoints />
        )}
        {routingPoints.length > 0 ? (
          <DraggableList handleDeleteItem={deleteFromRoute} items={routingPoints} setItems={setRoutingPoints} />
        ) : null}
        <Divider variant="middle" sx={{ my: 2 }} />

        {routingPoints.length === 1 ? <MapRouteBoxOnePoint /> : null}

        {!routeFound && routingPoints.length > 1 && !isLoading ? <MapRouteBoxNoRoute /> : null}

        <MapRouteInfo />
      </Box>

      <MapRouteProfile mode={layoutMode} />
      {routeData ? (
        <Box sx={{ px: 2 }}>
          <InfoPaneActionButtons>
            <InfoPaneShareActionButton object_type="route" feature={routeData.feature} />
            <InfoPaneCommentActionButton
              objekt_type="route"
              handleGoToComments={handleShowCommentPane}
              data={{ feature: routeData.feature }}
            />
            <InfoPaneGPXActionButton onClick={handleDownloadGPX} />
            <InfoPanePDFActionButton onClick={handleDownloadPDF} />
          </InfoPaneActionButtons>
        </Box>
      ) : null}

      {routeData ? (
        <>
          <Divider variant="middle" sx={{ pt: 2 }} />
          {routeWarnings.length > 0 ? <PaneWarnings warnings={routeWarnings} /> : null}
          <SidebarInfoBoxStaza />
        </>
      ) : null}
    </Box>
  );
}

export default PublicRoutesPane;
